import { useState } from 'react';

import styled from 'styled-components';
import { Pencil } from 'lucide-react';

import { GetDetailedOrderQuery, OrderState, TotadminPermission } from 'data/__generated__';

import { SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { renderAsPrice } from 'helpers/price';

import { OrderProductsPopup } from './OrderProductsPopup';

// Les mêmes états que `MODIFIABLE_ORDER_STATES` côté API : au-delà, la commande part en préparation.
const MODIFIABLE_ORDER_STATES = [OrderState.Created, OrderState.InProgress, OrderState.Validated, OrderState.Paused];

export const ProductsSection = ({ order }: { order: GetDetailedOrderQuery['detailedOrder'] }) => {
    const { productsWithQuantityPicked, productsRequestedButNotFullyDelivered } = order;
    const [isProductsPopupOpen, setIsProductsPopupOpen] = useState(false);

    const uniqueProducts = Array.from(
        new Map(
            [...productsWithQuantityPicked, ...productsRequestedButNotFullyDelivered].map((product) => [
                product._id.toString(),
                product,
            ]),
        ).values(),
    );

    const sortedProducts = uniqueProducts.sort((a, b) => a.name.localeCompare(b.name));

    const productIds = sortedProducts.map((product) => product._id);

    // Total quantity ordered
    const totalQuantityOrdered = productIds.reduce((acc, productId) => {
        const productWithQuantityPicked = productsWithQuantityPicked.find((product) => product._id === productId);
        const productWithQuantityRequested = productsRequestedButNotFullyDelivered.find(
            (product) => product._id === productId,
        );
        const quantityOrdered = productWithQuantityRequested
            ? productWithQuantityRequested.quantityRequested
            : productWithQuantityPicked?.quantity;
        return acc + (quantityOrdered ?? 0);
    }, 0);
    const totalPriceOrdered = productIds.reduce((acc, productId) => {
        const productWithQuantityPicked = productsWithQuantityPicked.find((product) => product._id === productId);
        const productWithQuantityRequested = productsRequestedButNotFullyDelivered.find(
            (product) => product._id === productId,
        );
        const quantityOrdered = productWithQuantityRequested
            ? productWithQuantityRequested.quantityRequested
            : productWithQuantityPicked?.quantity;
        return acc + (quantityOrdered ?? 0) * (productWithQuantityPicked?.unitPrice ?? 0);
    }, 0);
    const totalQuantityToPrepare = productsWithQuantityPicked.reduce(
        (acc, product) => acc + (product?.quantity ?? 0),
        0,
    );
    const totalPriceToPrepare = productsWithQuantityPicked.reduce(
        (acc, product) => acc + (product?.quantity ?? 0) * product.unitPrice,
        0,
    );
    const totalQuantityPicked = productsWithQuantityPicked.reduce(
        (acc, product) => acc + (product?.quantityPicked ?? 0),
        0,
    );
    const totalPricePicked = productsWithQuantityPicked.reduce(
        (acc, product) => acc + (product?.quantityPicked ?? 0) * product.unitPrice,
        0,
    );

    return (
        <SectionContainer title="Détails des produits" isInitiallyOpen>
            <SectionColumn>
                {MODIFIABLE_ORDER_STATES.includes(order.state) ? (
                    <EditRow>
                        <TotemPrimaryButton
                            type="button"
                            onClick={() => setIsProductsPopupOpen(true)}
                            data-test="edit-order-products"
                            requiredPermissions={[TotadminPermission.OrdersManage]}
                        >
                            <Pencil size={14} /> Modifier les produits
                        </TotemPrimaryButton>
                    </EditRow>
                ) : null}
                <OrderProductsPopup order={order} isOpen={isProductsPopupOpen} setIsOpen={setIsProductsPopupOpen} />
                <Table>
                    <thead>
                        <tr>
                            <HeaderCell>Nom</HeaderCell>
                            <HeaderCell>Prix (unité)</HeaderCell>
                            <HeaderCell>Qté commandée</HeaderCell>
                            <HeaderCell>Prix commandé (HT)</HeaderCell>
                            <HeaderCell>Qté à préparer</HeaderCell>
                            <HeaderCell>Prix à préparer (HT)</HeaderCell>
                            <HeaderCell>Qté livrée</HeaderCell>
                            <HeaderCell>Prix livré (HT)</HeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        {productIds.map((productId) => {
                            const productWithQuantityPicked = productsWithQuantityPicked.find(
                                (product) => product._id === productId,
                            );
                            const productWithQuantityRequested = productsRequestedButNotFullyDelivered.find(
                                (product) => product._id === productId,
                            );

                            const fullname =
                                productWithQuantityPicked?.fullname ?? productWithQuantityRequested?.fullname ?? '';
                            const unitPrice = productWithQuantityPicked?.unitPrice ?? 0;
                            const quantityOrdered = productWithQuantityRequested
                                ? productWithQuantityRequested.quantityRequested
                                : (productWithQuantityPicked?.quantity ?? 0);
                            const quantityToPrepare = productWithQuantityPicked?.quantity ?? 0;
                            const quantityPicked = productWithQuantityPicked?.quantityPicked;

                            const isQuantityToPrepareMismatch = quantityToPrepare !== quantityOrdered;
                            const isQuantityPickedMismatch =
                                quantityPicked != null && quantityPicked !== quantityOrdered;
                            const hasMismatch = isQuantityToPrepareMismatch || isQuantityPickedMismatch;

                            return (
                                <tr key={productId}>
                                    <td>
                                        <QuantityCell $isMismatch={hasMismatch}>{fullname}</QuantityCell>
                                    </td>
                                    <td>{renderAsPrice(unitPrice)}</td>
                                    <td>{quantityOrdered}</td>
                                    <td>{renderAsPrice(unitPrice * (quantityOrdered || 0))}</td>
                                    <td>
                                        <QuantityCell $isMismatch={isQuantityToPrepareMismatch}>
                                            {quantityToPrepare}
                                        </QuantityCell>
                                    </td>
                                    <td>{renderAsPrice(unitPrice * (quantityToPrepare || 0))}</td>
                                    <td>
                                        <QuantityCell $isMismatch={isQuantityPickedMismatch}>
                                            {quantityPicked}
                                        </QuantityCell>
                                    </td>
                                    <td>{quantityPicked ? renderAsPrice(unitPrice * quantityPicked) : '-'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <TableFooter>
                        <tr>
                            <td>TOTAL ({productsWithQuantityPicked.length} produits)</td>
                            <td />
                            <td>{totalQuantityOrdered}</td>
                            <td>{renderAsPrice(totalPriceOrdered)}</td>
                            <td>{totalQuantityToPrepare}</td>
                            <td>{renderAsPrice(totalPriceToPrepare)}</td>
                            <td>{totalQuantityPicked}</td>
                            <td>{renderAsPrice(totalPricePicked)}</td>
                        </tr>
                    </TableFooter>
                </Table>
            </SectionColumn>
        </SectionContainer>
    );
};

const Table = styled.table`
    border-spacing: 0 4px;
    border-collapse: separate;

    thead th {
        padding: 6px 12px 6px 0;
        padding-bottom: 10px;
    }

    tbody td {
        padding: 8px 12px 8px 0;
        border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    }
`;

const HeaderCell = styled.th`
    text-align: left;
`;

const TableFooter = styled.tfoot`
    & td {
        font-weight: 800;
        border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
        padding: 10px 12px 10px 0;
    }
`;

const QuantityCell = styled.span<{ $isMismatch: boolean }>`
    ${({ $isMismatch, theme }) => $isMismatch && `color: ${theme.errorColor};`}
    font-weight: ${({ $isMismatch }) => ($isMismatch ? 800 : 500)};
`;

const EditRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 10px;
`;
